/**
 * Tiny WebVTT codec used by the captions editor: parse a VTT string
 * into cue objects with start/end (seconds) + text, and serialize
 * back. Tolerant on input: handles CRLF, optional cue identifiers,
 * and multi-line cue text. Strict on output: emits the canonical
 * `WEBVTT\n\n` header and `HH:MM:SS.mmm` timestamps.
 *
 * Not a full WebVTT implementation — no cue settings, regions,
 * styling, etc. The captions pipeline doesn't use those features
 * and the operator editor doesn't need to round-trip them.
 */

// One regex for both the single-timestamp parser and the cue-line parser.
const TS_FIELDS = /(\d{2,}):(\d{2}):(\d{2}\.\d{3})/;
const TS_PATTERN = new RegExp("^" + TS_FIELDS.source + "\\s*-->\\s*" + TS_FIELDS.source);

function tsFieldsToSeconds(h, m, s) {
  return parseInt(h, 10) * 3600 + parseInt(m, 10) * 60 + parseFloat(s);
}

export function parseTimestamp(s) {
  const m = String(s)
    .trim()
    .match(new RegExp("^" + TS_FIELDS.source + "$"));
  if (!m) return 0;
  return tsFieldsToSeconds(m[1], m[2], m[3]);
}

export function formatTimestamp(seconds) {
  const t = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t - h * 3600 - m * 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${s.toFixed(3).padStart(6, "0")}`;
}

/**
 * Parse a VTT document. Returns { cues, error } — error is null on
 * success, or a short string the UI can show. Cues have:
 *   { id: number, start: float, end: float, text: string }
 */
export function parseVTT(text) {
  if (typeof text !== "string" || text.trim().length === 0) {
    return { cues: [], error: "Empty document." };
  }
  const normalized = text.replace(/\r\n?/g, "\n");
  const lines = normalized.split("\n");

  // First non-empty line must be "WEBVTT".
  let i = 0;
  while (i < lines.length && lines[i].trim() === "") i++;
  if (i >= lines.length || !/^WEBVTT(\s|$)/.test(lines[i])) {
    return { cues: [], error: 'Missing "WEBVTT" header.' };
  }
  i++;

  const cues = [];
  while (i < lines.length) {
    while (i < lines.length && lines[i].trim() === "") i++;
    if (i >= lines.length) break;

    // Optional cue identifier line (no `-->`).
    if (!lines[i].includes("-->")) {
      i++;
      if (i >= lines.length) break;
    }

    const ts = lines[i].match(TS_PATTERN);
    if (!ts) {
      // Skip malformed cue, advance to next blank.
      while (i < lines.length && lines[i].trim() !== "") i++;
      continue;
    }
    const start = tsFieldsToSeconds(ts[1], ts[2], ts[3]);
    const end = tsFieldsToSeconds(ts[4], ts[5], ts[6]);
    i++;

    const textLines = [];
    while (i < lines.length && lines[i].trim() !== "") {
      textLines.push(lines[i]);
      i++;
    }
    cues.push({ id: cues.length, start, end, text: textLines.join("\n") });
  }

  if (cues.length === 0) {
    return { cues: [], error: "No cues found." };
  }
  return { cues, error: null };
}

/**
 * Sanitize cue text so the round-trip serialize → parse is lossless.
 * WebVTT terminates a cue at the first blank line, so any blank or
 * whitespace-only line inside the text would silently truncate the
 * cue on the next load. Split, drop every whitespace-only line, and
 * rejoin — this collapses runs of any length (a regex-based collapse
 * couldn't because consecutive whitespace-only lines stop sharing
 * the leading `\n` after the first replacement). Outer whitespace is
 * trimmed at the end.
 */
function sanitizeCueText(text) {
  return String(text ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter(line => line.trim() !== "")
    .join("\n")
    .trim();
}

/**
 * Serialize cues back to a canonical VTT document.
 */
export function serializeVTT(cues) {
  const lines = ["WEBVTT", ""];
  for (const cue of cues) {
    lines.push(`${formatTimestamp(cue.start)} --> ${formatTimestamp(cue.end)}`);
    lines.push(sanitizeCueText(cue.text));
    lines.push("");
  }
  return lines.join("\n");
}

